import classNames from 'classnames';
import {
  CSSProperties,
  Dispatch,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import Dropzone, { DropzoneRootProps, FileRejection } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  doFormMsg,
  FormMessage,
  FormMsg,
  NoFormMsg,
} from 'components/forms/FormMessage';
import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import { useConfirmDelete } from 'components/modal/forms/ConfirmDeleteForm';
import {
  AcceptedFileType,
  AcceptedMimeType,
  getFileTypeInfo,
  getImageTypeInfo,
  isImageType,
  isVideoType,
  sizeAllowed,
  typesAllowed,
} from 'features/files/fileSlice';
import { MAX_FILENAME_LENGTH } from 'helpers/consts';

import './FileUpload.scss';

export type ImageSize = {
  width: number;
  height: number;
};
type PreviewProps = {
  previewSrc: string | null;
  defaultImg?: React.JSX.Element | null;
  size?: ImageSize;
};

function Preview({ previewSrc, defaultImg, size }: PreviewProps) {
  const [imgSize, setImgSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    var image = new Image();
    if (previewSrc) {
      image.src = previewSrc;
      // console.log('image.height: ', image.height);
      setImgSize({ width: image.width, height: image.height });
    }
  }, [previewSrc]);

  return previewSrc ? (
    <div
      className="preview_image"
      style={{
        backgroundImage: `url("${previewSrc}")`,
        height: size?.height ?? imgSize.width,
        width: size?.width ?? imgSize.height,
      }}
    />
  ) : (
    <div className="preview_image">
      {defaultImg !== undefined ? (
        defaultImg
      ) : (
        <Icon symbol={IconSymbol.user} className="default_user" />
      )}
    </div>
  );
}

type FIleUploadProps = {
  types: AcceptedFileType[];
  currentSrc: string | null;
  noPreview?: boolean;
  previewSrc?: string | null;
  setPreviewSrc?: Dispatch<SetStateAction<string | null>>;
  previewIcon?: IconSymbol | null;
  setPreviewIcon?: Dispatch<SetStateAction<IconSymbol | null>>;
  previewMimeType?: AcceptedMimeType;
  setPreviewMimeType?: Dispatch<SetStateAction<AcceptedMimeType>>;
  defaultImg?: React.JSX.Element | null;
  placeholder?: React.JSX.Element | null;
  showMsg?: (m: FormMsg) => void;
  setFile: Dispatch<SetStateAction<File | undefined>>;
  onDelete: () => void;
  onSave?: () => void;
  saved?: boolean;
  confirmationText?: string | React.JSX.Element;
  withTrigger?: boolean;
  showTrigger?: boolean; // show trigger also when no preview available
  className?: string;
  disabled?: boolean;
  renderTrigger?: (
    onClick: MouseEventHandler,
    getInputProps: () => DropzoneRootProps,
  ) => void;
  size?: number;
  style?: CSSProperties;
  children?: ReactNode;
};

export default function FileUpload(props: FIleUploadProps & PropsWithChildren) {
  const {
    types,
    showMsg,
    setFile,
    currentSrc,
    previewSrc,
    setPreviewSrc,
    previewIcon,
    setPreviewIcon,
    noPreview,
    previewMimeType,
    setPreviewMimeType,
    defaultImg,
    placeholder,
    onDelete,
    onSave,
    saved,
    confirmationText,
    withTrigger = true,
    showTrigger = false,
    className,
    disabled = false,
    renderTrigger,
    style,
    children,
  } = props;
  const [imgDropped, setImgDropped] = useState(false);
  const dropRef = useRef<HTMLDivElement | null>(null); // ref for managing the hover state of droppable area
  const [mimeType, setMimeType] = useState<AcceptedMimeType>(
    AcceptedMimeType.jpg,
  );
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const intl = useIntl();
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const showFormMsg = showMsg || doFormMsg;
  const confirmDelete = useConfirmDelete();

  useEffect(() => {
    const imgType = getImageTypeInfo(previewMimeType ?? mimeType);
    if (previewSrc) {
      if (imgType) {
        const image = new Image();
        image.onload = () => {
          setSize({ width: image.width, height: image.height });
        };
        image.src = previewSrc;
      } else {
        setSize({ width: 48, height: 48 });
      }
    } else {
      setSize({ width: 0, height: 0 });
    }
  }, [mimeType, previewMimeType, previewSrc]);

  function onDrop(acceptedFiles: File[], fileRejections: FileRejection[]) {
    doFormMsg(NoFormMsg, setFormMsg);
    if (acceptedFiles.length) {
      const [uploadedFile] = acceptedFiles;
      const max = sizeAllowed(types, uploadedFile.type);
      if (uploadedFile.size > max) {
        showFormMsg(
          {
            message: intl.formatMessage(
              { id: 'FILES.UPLOAD.TOO_LARGE' },
              { sizeallowed: max / 1_000_000 },
            ),
            success: false,
          },
          setFormMsg,
        );
      } else {
        // Create a new File with a too long file name cut off, to avoid backup issues.
        // In Azure, our backend gives the file its own unique folder, so
        // no need to check for duplicates.
        const originalName = uploadedFile.name;
        const lastDot = originalName.lastIndexOf('.');
        let base = originalName;
        let ext = '';
        if (lastDot > 0 && lastDot < originalName.length - 1) {
          base = originalName.slice(0, lastDot);
          ext = originalName.slice(lastDot); // includes the dot
        }
        const maxBaseLength = MAX_FILENAME_LENGTH - ext.length;
        const truncatedBase = base.slice(0, maxBaseLength);
        const newFileName = truncatedBase + ext;
        const renamedFile = new File([uploadedFile], newFileName, {
          type: uploadedFile.type,
          lastModified: uploadedFile.lastModified,
        });
        setFile(renamedFile);
        const ftInfo = getFileTypeInfo(renamedFile);
        setPreviewMimeType
          ? setPreviewMimeType(ftInfo.mime)
          : setMimeType(ftInfo.mime);
        if (isImageType(ftInfo.mime)) {
          const fileReader = new FileReader();
          fileReader.onload = () => {
            setPreviewSrc && setPreviewSrc(fileReader.result as string);
            // var image = new Image();
            // image.src = fileReader.result as string;
            // setSize({ width: image.width, height: image.height });
          };
          fileReader.readAsDataURL(renamedFile);
        }
        if (isVideoType(ftInfo.mime)) {
          console.log('video!');
          // creating a video element
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.src = URL.createObjectURL(renamedFile);
          video.crossOrigin = 'anonymous'; //allowing video processing

          // handling possible video loading issues
          video.addEventListener('error', (e) => {
            console.error('Video loading error:', e);
          });

          // loading the video
          video.addEventListener('loadedmetadata', () => {
            // ensuring chosen millisecond is within the video's duration
            const chosenMillisecond = 0.001;
            if (chosenMillisecond > video.duration) {
              console.error('Chosen millisecond exceeds video duration');
              return;
            }

            //creating canvas
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const canvasContext = canvas.getContext('2d');
            if (canvasContext) {
              //setting the timeframe
              video.currentTime = chosenMillisecond;
              video.addEventListener('seeked', () => {
                // handling errors for canvas context
                if (!canvasContext) {
                  console.error('Failed to get canvas context');
                  return;
                }

                // drawing frame on canvas
                canvasContext.drawImage(
                  video,
                  0,
                  0,
                  canvas.width,
                  canvas.height,
                );
                const thumbnailImage = canvas.toDataURL('image/png');
                setPreviewSrc && setPreviewSrc(thumbnailImage);

                // freeing up memory
                URL.revokeObjectURL(video.src);
                video.remove();
              });
            }
          });
        } else {
          setPreviewSrc && setPreviewSrc(null);
          setPreviewIcon && setPreviewIcon(ftInfo.icon);
        }
        setImgDropped(true);
      }
    } else if (fileRejections.length > 0) {
      showFormMsg(
        {
          message: intl.formatMessage(
            { id: 'FILES.UPLOAD.WRONG_TYPE' },
            { typesallowed: typesAllowed(types) },
          ),
          success: false,
        },
        setFormMsg,
      );
    }
    if (dropRef.current) dropRef.current.classList.remove('targeted');
  }

  // update border on hover over file drop zone
  const updateStyle = (
    e: React.DragEvent<HTMLElement>,
    draggedOver: boolean,
  ) => {
    if (!dropRef.current) return;
    if (
      !draggedOver &&
      dropRef.current.contains(e.relatedTarget as HTMLElement)
    )
      return;
    if (draggedOver) dropRef.current.classList.add('targeted');
    else dropRef.current.classList.remove('targeted');
  };

  async function removeImage(e: React.MouseEvent<HTMLDivElement>) {
    showFormMsg(NoFormMsg, setFormMsg);
    e.stopPropagation();
    function doDelete(): boolean {
      if (imgDropped) {
        // restore the original preview if there was one
        setPreviewSrc && setPreviewSrc(currentSrc);
        setImgDropped(false);
      } else {
        // remove the preview
        setPreviewSrc && setPreviewSrc(null);
        // tell the parent to delete the file
        onDelete && onDelete();
      }
      // remove any uploaded file
      setFile(undefined);
      return true;
    }
    if (!imgDropped) {
      // we're deleting the current file, please confirm
      const confirmed = await confirmDelete({
        prompts: {
          prompt1: confirmationText || (
            <FormattedMessage id="GENERAL.DELETE.FILE.CNF" />
          ),
          prompt2: null,
        },
      });
      if (confirmed) {
        doDelete();
      }
    } else doDelete(); // we're only deleting a candidate upload, so just do it
  }

  return (
    <>
      <Dropzone
        accept={types.reduce((acc, t) => ({ ...acc, [t.mime]: t.ext }), {})}
        onDrop={onDrop}
        onDragEnter={(e: React.DragEvent<HTMLElement>) => updateStyle(e, true)}
        // onDragOver={(e: React.DragEvent<HTMLElement>) => updateStyle(e, true)}
        onDragLeave={(e: React.DragEvent<HTMLElement>) => updateStyle(e, false)}
        noClick={withTrigger || !!renderTrigger}
        disabled={disabled}
        multiple={false}
        useFsAccessApi={true}
      >
        {({ getRootProps, getInputProps, open }) => {
          if (renderTrigger) renderTrigger(open, getInputProps);
          return (
            <>
              <div
                {...getRootProps({
                  className: classNames(className, 'dropzone', {
                    dropzone_image: !!previewSrc,
                  }),
                })}
                ref={dropRef}
              >
                {noPreview ? null : (
                  <div className="dropzone_container">
                    {previewSrc ? (
                      <div
                        className="preview_image"
                        style={{
                          backgroundImage: `url("${previewSrc}")`,
                          height:
                            props.size ??
                            (size.height ? size.height : undefined),
                          width:
                            props.size ?? (size.width ? size.width : undefined),
                          ...style,
                        }}
                      />
                    ) : (
                      <div
                        className="preview_image"
                        style={{
                          height:
                            props.size ??
                            (size.height ? size.height : undefined),
                          width:
                            props.size ?? (size.width ? size.width : undefined),
                          ...style,
                        }}
                      >
                        {defaultImg !== undefined ? (
                          defaultImg
                        ) : previewIcon ? (
                          <Icon
                            symbol={previewIcon ?? IconSymbol.mime_unknown}
                            size={props.size}
                          />
                        ) : null}
                      </div>
                    )}
                    <>
                      {formMsg ? (
                        <FormMessage
                          message={formMsg.message}
                          success={formMsg.success}
                          timeout={5000}
                          setter={setFormMsg}
                        />
                      ) : null}
                    </>
                    <input {...getInputProps()} />
                    <div className="dropzone_icons">
                      {!disabled &&
                      withTrigger &&
                      (showTrigger || previewSrc) ? (
                        <>
                          <Icon
                            symbol={IconSymbol.pencil}
                            onClick={open}
                            className="dropzone_trigger"
                            size={20}
                          />
                          {/* <div
                          onClick={open}
                          className="dropzone_trigger"
                          style={{ cursor: 'Pointer' }}
                        /> */}
                        </>
                      ) : null}
                      {!disabled && previewSrc && (
                        <>
                          <Icon
                            symbol={IconSymbol.bin}
                            onClick={removeImage}
                            className="dropzone_bin"
                            size={20}
                          />
                          {/* <div className="dropzone_bin" onClick={removeImage} /> */}
                        </>
                      )}
                      {!disabled &&
                      previewSrc &&
                      imgDropped &&
                      onSave &&
                      !saved ? (
                        <>
                          <Icon
                            symbol={IconSymbol.upload}
                            onClick={onSave}
                            className="dropzone_save"
                            size={20}
                          />
                        </>
                      ) : null}
                    </div>
                    {!disabled && !previewSrc && placeholder ? (
                      <>{placeholder}</>
                    ) : null}
                  </div>
                )}
                {children}
              </div>
            </>
          );
        }}
      </Dropzone>
    </>
  );
}

FileUpload.Preview = Preview;
